import { useState } from 'react';
import type { FormEvent, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { invitableDomain, isInvitableAddress, useIdentity } from '@/entities/session';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { SelectField, TextField } from '@/shared/ui/Field';

import { useAddUser } from '../api/auth';
import type { AddUserResponse } from '@/shared/api/generated/tenancy';

/* The four provisioning creates, each with a sentence explaining what it is for. Keyed by name so a
 * custom role falls through to its own name rather than to one of these. */
const STARTER_LABEL: Readonly<Record<string, string>> = {
  reader: 'auth.team.role.reader',
  'data-steward': 'auth.team.role.steward',
  approver: 'auth.team.role.approver',
  admin: 'auth.team.role.admin',
};

/* An admin adding a teammate to their own tenant (ADR-012, `POST /v1/auth/users`).
 *
 * Role defaults to `reader`, the least privileged — the server's own default too, so a client that
 * omitted the field would get the same answer. An admin must deliberately pick a wider role rather
 * than an empty select silently meaning one.
 *
 * **It renders the fields and nothing around them — no card, no heading.** It used to own a `Card`
 * carrying "Add a teammate" and "Local email/password accounts only", which is right for a bare page
 * and a duplicated heading inside a `Modal` that announces the same two strings as its own name and
 * hint. The chrome belongs to whoever is placing the form: `AdminScreen` passes those two keys to
 * `Modal`, `TeamScreen` passes them to the `Card` it now owns. One set of copy keys, two frames. */
export interface AddUserFormProps {
  /** Called after the server accepts the new user, so a host screen can close the panel and
   * refetch its listing. The form still renders its own confirmation - the callback is in addition
   * to that, not instead of it. */
  readonly onAdded?: (() => void) | undefined;
  /** The roles this tenant actually has, from `GET /v1/auth/roles`.
   *
   * **Defaults to the four starters when absent**, which is what this form used to hardcode. That
   * was correct for every tenant until one adds a custom role, at which point the form could not
   * offer it and an administrator had to create the user and then change their role from the Admin
   * screen. Passing the real list removes that second step. The default stays so a caller with no
   * role list - the standalone Team screen - still works. */
  readonly roleNames?: readonly string[] | undefined;
  /** Dismiss without submitting. **Absent means no Cancel button is rendered**, which is the
   * standalone-page case: a Cancel on `/team` has nowhere to go, and a control that does nothing is
   * worse than a missing one. Only a caller that can actually close the form asks for it. */
  readonly onCancel?: (() => void) | undefined;
}

const STARTER_ROLES: readonly string[] = ['reader', 'data-steward', 'approver', 'admin'];

export function AddUserForm({
  onAdded,
  roleNames,
  onCancel,
}: AddUserFormProps = {}): ReactElement {
  const { t } = useTranslation();
  const addUser = useAddUser();
  /* The mail domain this administrator may add colleagues at, from their own verified subject.
   *
   * `undefined` while `/v1/auth/me` is still loading and for a subject that is not an email, and
   * both must read as "say nothing" rather than "allow nothing" — the server is what refuses, and
   * a form that blocked submission on a value it could not compute would break the flow rather
   * than guard it. See `entities/session/model/emailDomain.ts`. */
  const allowedDomain = invitableDomain(useIdentity().data);
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  /* A plain string rather than the four starter names as a union: a tenant may have custom roles,
   * and the server validates the name against its own `authz.role` rows anyway - refusing an
   * unknown one with a 422 that names it. A narrower type here would only stop this form offering a
   * role the tenant really has. */
  const [roleName, setRoleName] = useState<string>('reader');
  /* The whole response, not just the address. `invitation_email` is what decides whether the
   * administrator still has to pass the activation link on themselves, and that is the one thing
   * they need to know before they close this dialog. */
  const [added, setAdded] = useState<AddUserResponse | null>(null);
  /* Set when this form declines to submit, cleared the moment the address changes. **Cleared on
   * change deliberately**: a refusal that outlived the value it was about would sit under a
   * corrected address and read as a refusal of *that* one, which is the defect CLAUDE.md §9
   * records on the rulegen screen where a failed generation's message survived a dataset change. */
  const [domainRefusal, setDomainRefusal] = useState<string | null>(null);

  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setAdded(null);
    setDomainRefusal(null);
    if (email.trim() === '') return;
    /* **A courtesy, not the control.** `TeamInviteService.add_user` calls
     * `refuse_a_foreign_domain` before it writes anything, keyed on the actor's verified token
     * subject, so a client that skipped this — or called the API directly — is refused with a 422
     * naming the domain. What this buys is that a mistyped address is answered without a round
     * trip and without an audit-worthy refusal on the server. */
    if (!isInvitableAddress(email.trim(), allowedDomain)) {
      setDomainRefusal(t('auth.team.foreignDomain', { domain: allowedDomain ?? '' }));
      return;
    }
    addUser.mutate(
      {
        email: email.trim(),
        display_name: displayName.trim(),
        role_name: roleName,
      },
      {
        onSuccess: (result) => {
          setEmail('');
          setDisplayName('');
          setAdded(result);
          onAdded?.();
        },
      },
    );
  };

  return (
    <form onSubmit={onSubmit}>
      <TextField
        label={t('auth.field.email')}
        type="text"
        value={email}
        onChange={(next) => {
          setEmail(next);
          setDomainRefusal(null);
        }}
        /* Absent rather than a placeholder sentence when the domain is unknown: a hint that said
         * "must match your own domain" without naming it would make an administrator guess at the
         * exact string, which is the one thing the hint exists to remove. */
        hint={
          allowedDomain === undefined
            ? undefined
            : t('auth.team.domainHint', { domain: allowedDomain })
        }
        required
      />
      <TextField
        label={t('auth.field.displayName')}
        value={displayName}
        onChange={setDisplayName}
      />
      {/* **There is no password field, and its absence is the feature.**
          An administrator supplies a name, an address and a role; the platform mails a 48-hour
          single-use activation link and the invitee chooses the only password the account will
          ever have. So an administrator cannot set one, cannot see one, and is never asked to
          pass one on — which is a property of what this form *can send* rather than of a rule
          anybody follows. `AddUserRequest` has no `password` property either. */}
      <SelectField
        label={t('auth.team.role')}
        value={roleName}
        onChange={setRoleName}
        options={(roleNames ?? STARTER_ROLES).map((name) => {
          /* A starter role gets its curated sentence; a custom one gets its own name, because
             nothing here knows what a tenant meant by it and inventing a description would be
             worse than showing none. Bound to a local first so the narrowing survives - indexing
             twice reads as two lookups to the type checker and needed a cast to agree. */
          const labelKey = STARTER_LABEL[name];
          return { value: name, label: labelKey === undefined ? name : t(labelKey) };
        })}
      />
      {/* One slot for both refusals, so a reader never has to work out which of two error
          regions is speaking. The client-side one wins when present: it is the more specific
          answer, and the request it prevented cannot also have failed. */}
      {domainRefusal !== null && (
        <Alert tone="error" title={t('auth.team.failedTitle')}>
          {domainRefusal}
        </Alert>
      )}
      {domainRefusal === null && addUser.isError && (
        <Alert tone="error" title={t('auth.team.failedTitle')}>
          {addUser.error.message}
        </Alert>
      )}
      {added !== null && (
        /* **The tone follows the delivery, not the creation.** The account exists in all three
           cases, so a plain success notice would be true and useless: with no mail transport, or a
           relay that refused, nobody has sent this person their activation link and only the
           administrator can resend it. `failed` and `none` are separated because they are fixed by different people — a
           relay outage against an operator who set no host — and one message covering both would
           send somebody to ask the wrong one.

           **`restored` picks a different sentence rather than adding one**, because it changes what
           the whole message means: the account was not created, its history is still attached to
           the person, and the roles it held before were revoked when it was retired — so the only
           grant in force is the one just chosen. An administrator who read "added" would not know
           to check any of that. Six messages rather than a suffix, because "was added" and "was
           restored" are different clauses in the middle of a sentence, not a phrase bolted on. */
        <Alert tone={added.invitation_email === 'sent' ? 'notice' : 'warning'}>
          {t(
            `auth.team.${added.restored ? 'restored' : 'added'}.${added.invitation_email}`,
            { email: added.subject, hours: added.expires_in_hours },
          )}
        </Alert>
      )}
      {/* Cancel first, in the markup *and* on screen, which is `.lq-confirm__actions`' recorded
          reasoning borrowed rather than re-argued: the answer that changes nothing reads first and is
          the first tab stop, and the one that writes goes last. `.lq-form-actions` rather than that
          class, because this is a form's row and that one is a confirmation's — they differ in
          alignment, and reusing the confirm class would make a later change to how a *question* lays
          out its answers silently move this form's buttons. */}
      <div className="lq-form-actions">
        {onCancel === undefined ? null : (
          <Button type="button" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
        )}
        <Button type="submit" tone="primary" busy={addUser.isPending}>
          {t('auth.team.submit')}
        </Button>
      </div>
    </form>
  );
}
